import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAppContext } from '../../context/AppContext';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

export default function Devices() {
  const { devices, linkDevice, unlinkDevice, profile, products, claimFreeDevice } = useAppContext();
  const [showActivate, setShowActivate] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEligibleForFree = profile?.subscription_tier === 'yearly' && !profile?.has_claimed_free_device;

  const handleActivate = async () => {
    if (code.length < 6) return;
    setLoading(true);
    setError('');
    const { error: linkError } = await linkDevice(code, 'My TapBadge');
    if (linkError) {
      setError(typeof linkError === 'string' ? linkError : 'Invalid or already linked code.');
    } else {
      setShowActivate(false);
      setCode('');
    }
    setLoading(false);
  };

  const handleClaim = async (productId) => {
    setLoading(true);
    const { success, error } = await claimFreeDevice(productId);
    setLoading(false);
    if (success) {
      setShowClaimModal(false);
      alert("Claim successful! Your free device has been added to your orders and will be shipped shortly.");
    } else {
      alert(error.message || "Failed to claim device.");
    }
  };

  const handleDeactivate = async (id) => {
    if (confirm('Deactivating this device will disconnect it from your profile. You can reactivate it later or give it to someone else. Proceed?')) {
      await unlinkDevice(id);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Devices</h1>
          <p className="text-gray-500 mt-1">Manage physical TapBadges linked to your profile.</p>
        </div>
        <div className="flex gap-3">
          {isEligibleForFree && (
            <Button onClick={() => setShowClaimModal(true)} variant="secondary" className="border-indigo-200 text-indigo-600 bg-indigo-50">
              <SafeIcon name="Gift" className="mr-2" /> Claim Free Device
            </Button>
          )}
          <Button onClick={() => setShowActivate(!showActivate)} variant={showActivate ? 'secondary' : 'primary'}>
            <SafeIcon name={showActivate ? "X" : "Plus"} className="mr-2" /> {showActivate ? 'Cancel' : 'Activate Device'}
          </Button>
        </div>
      </div>

      {showActivate && (
        <div className="mb-8 p-6 bg-indigo-50 rounded-2xl border border-indigo-100 animate-in fade-in slide-in-from-top-4 duration-300">
          <h3 className="font-semibold text-indigo-900 mb-2">Activate a new TapBadge</h3>
          <p className="text-sm text-indigo-700 mb-4">Enter the 6-digit activation code found on your device packaging.</p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md">
            <input type="text" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. A8F4B2" className="flex-1 px-4 py-3 border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 uppercase tracking-widest font-bold text-center text-lg" />
            <Button onClick={handleActivate} disabled={loading || code.length < 6} className="px-8">
              {loading ? 'Linking...' : 'Link Device'}
            </Button>
          </div>
          {error && <p className="text-red-600 text-sm mt-3 font-medium flex items-center gap-1"><SafeIcon name="AlertCircle" /> {error}</p>}
        </div>
      )}

      {devices.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-gray-400 mx-auto mb-4 shadow-sm">
            <SafeIcon name="Smartphone" className="text-2xl" />
          </div>
          <h3 className="text-gray-900 font-bold">No active devices</h3>
          <p className="text-gray-500 max-w-xs mx-auto mt-2"> Link your first TapBadge to start sharing your profile with a simple tap. </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {devices.map(device => (
            <div key={device.id} className="group border border-gray-100 bg-white rounded-3xl p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
                    <SafeIcon name={device.type === 'card' ? 'CreditCard' : 'Key'} className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{device.name}</h3>
                    <p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">{device.serial_number}</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-green-100 text-green-700 uppercase"> Active </span>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                <p className="text-xs text-gray-400"> Linked {new Date(device.linked_at).toLocaleDateString()} </p>
                <button onClick={() => handleDeactivate(device.id)} className="text-xs font-bold text-red-500 hover:text-red-700 transition-colors flex items-center gap-1" >
                  <SafeIcon name="Trash2" /> Deactivate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Claim Free Device Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] p-8 w-full max-w-2xl shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Claim Your Reward</h2>
                <p className="text-gray-500">Choose one free device as a yearly pro member.</p>
              </div>
              <button onClick={() => setShowClaimModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><SafeIcon name="X" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              {products.map(product => (
                <div key={product.id} className="p-4 border border-gray-100 rounded-2xl bg-gray-50 flex items-center gap-4 group hover:border-indigo-300 transition-all">
                  <img src={product.image_url} className="w-16 h-16 rounded-xl object-cover" alt="" />
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-sm">{product.name}</h4>
                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">{product.type}</p>
                    <button 
                      onClick={() => handleClaim(product.id)}
                      disabled={loading}
                      className="mt-2 text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    >
                      Choose this <SafeIcon name="ArrowRight" size={10} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
